import type { Game } from "./types.js";

type InvaderType = "squid" | "crab" | "octopus";

interface Invader {
  alive: boolean;
  type: InvaderType;
  points: number;
  row: number;
  col: number;
}

interface Bullet {
  x: number;
  y: number;
  w: number;
  h: number;
  vy: number;
  from: "player" | "enemy";
  wiggle?: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  age: number;
  color: string;
  size: number;
}

interface Floater {
  x: number;
  y: number;
  text: string;
  color: string;
  age: number;
  life: number;
}

interface StarP {
  x: number;
  y: number;
  s: number;
  v: number;
}

type StateValue =
  | "title"
  | "play"
  | "paused"
  | "lifeLost"
  | "waveClear"
  | "gameOver";

const SPRITES: Record<"squid" | "crab" | "octopus", string[][]> & {
  player: string[];
  ufo: string[];
  blast: string[];
} = {
  squid: [
    [
      "0011110000",
      "0111111100",
      "1111111111",
      "1100110011",
      "1111111111",
      "0010110100",
      "0100001010",
      "0010010100",
    ],
    [
      "0011110000",
      "0111111100",
      "1111111111",
      "1100110011",
      "1111111111",
      "0010110100",
      "0101001010",
      "1010000101",
    ],
  ],
  crab: [
    [
      "0010000100",
      "0001001000",
      "0011111100",
      "0110110110",
      "1111111111",
      "1011111101",
      "1010000101",
      "0001101100",
    ],
    [
      "0010000100",
      "1001001001",
      "1011111101",
      "1110110111",
      "1111111111",
      "0111111110",
      "0010000100",
      "0100000010",
    ],
  ],
  octopus: [
    [
      "0001111100",
      "0111111111",
      "1111111111",
      "1110011100",
      "1111111111",
      "0011111100",
      "0110001100",
      "1100000011",
    ],
    [
      "0001111100",
      "0111111111",
      "1111111111",
      "1110011100",
      "1111111111",
      "0011111100",
      "0100110010",
      "0011001100",
    ],
  ],
  player: [
    "0000010000",
    "0000111000",
    "0000111000",
    "0111111110",
    "1111111111",
    "1111111111",
    "1111111111",
    "1111111111",
  ],
  ufo: [
    "00001111110000",
    "00111111111100",
    "01111111111110",
    "11011011011011",
    "11111111111111",
    "00111001110000",
    "00010000010000",
  ],
  blast: [
    "0010001000100",
    "1001000010010",
    "0100100001001",
    "0010100010100",
    "1111111111111",
    "0010100010100",
    "0100100001001",
    "1001000010010",
    "0010001000100",
  ],
};

const BUNKER_SHAPE = [
  "0001111111111000",
  "0011111111111100",
  "0111111111111110",
  "1111111111111111",
  "1111111111111111",
  "1111111111111111",
  "1111100000011111",
  "1110000000000111",
];
const BUNKER_CELL = 3;
const BUNKER_COLS = BUNKER_SHAPE[0]!.length;
const BUNKER_ROWS = BUNKER_SHAPE.length;

function spriteWidth(s: string[]): number { return s[0]!.length; }
function spriteHeight(s: string[]): number { return s.length; }

let wrap: HTMLDivElement | null = null;
let cleanupFn: (() => void) | null = null;

function buildDom(host: HTMLElement): {
  wrap: HTMLDivElement;
  canvas: HTMLCanvasElement;
  overlayEl: HTMLDivElement;
  startBtn: HTMLButtonElement;
} {
  const wrapEl = document.createElement("div");
  wrapEl.className = "space-invaders-wrap";

  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 720;
  canvas.className = "space-invaders-canvas";
  canvas.setAttribute("aria-hidden", "true");

  const overlayEl = document.createElement("div");
  overlayEl.className = "space-invaders-overlay";
  overlayEl.innerHTML = `
    <h1 class="space-invaders-overlay__title">SPACE INVADERS</h1>
    <p><kbd>&larr;</kbd> <kbd>&rarr;</kbd> move &nbsp; <kbd>Space</kbd> shoot</p>
    <p><kbd>P</kbd> pause &nbsp; <kbd>M</kbd> mute</p>
    <button class="space-invaders-overlay__btn" type="button">START</button>
  `;

  const startBtn = overlayEl.querySelector<HTMLButtonElement>(".space-invaders-overlay__btn")!;

  wrapEl.appendChild(canvas);
  wrapEl.appendChild(overlayEl);
  host.appendChild(wrapEl);

  return { wrap: wrapEl, canvas, overlayEl, startBtn };
}

function mount(host: HTMLElement): void {
  const dom = buildDom(host);
  wrap = dom.wrap;
  const { canvas, overlayEl, startBtn } = dom;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;

  const W = canvas.width;
  const H = canvas.height;

  // ---------- Audio (Web Audio synthesised SFX) ----------
  const audio = (() => {
    type AC = AudioContext;
    let ctxA: AC | null = null;
    let muted = false;
    const ensure = (): void => {
      if (!ctxA) {
        try {
          const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
          const Ctor = W.AudioContext ?? W.webkitAudioContext;
          if (Ctor) ctxA = new Ctor();
        } catch {
          ctxA = null;
        }
      }
      if (ctxA && ctxA.state === "suspended") void ctxA.resume();
    };
    const blip = (freq: number, dur: number, type: OscillatorType = "square", vol = 0.08, slideTo: number | null = null): void => {
      if (muted) return;
      ensure();
      if (!ctxA) return;
      const o = ctxA.createOscillator();
      const g = ctxA.createGain();
      o.type = type;
      o.frequency.setValueAtTime(freq, ctxA.currentTime);
      if (slideTo !== null) {
        o.frequency.exponentialRampToValueAtTime(slideTo, ctxA.currentTime + dur);
      }
      g.gain.setValueAtTime(vol, ctxA.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctxA.currentTime + dur);
      o.connect(g).connect(ctxA.destination);
      o.start();
      o.stop(ctxA.currentTime + dur);
    };
    const noise = (dur: number, vol = 0.15): void => {
      if (muted) return;
      ensure();
      if (!ctxA) return;
      const bufferSize = Math.floor(ctxA.sampleRate * dur);
      const buf = ctxA.createBuffer(1, bufferSize, ctxA.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const src = ctxA.createBufferSource();
      src.buffer = buf;
      const g = ctxA.createGain();
      g.gain.setValueAtTime(vol, ctxA.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctxA.currentTime + dur);
      const f = ctxA.createBiquadFilter();
      f.type = "lowpass";
      f.frequency.value = 1200;
      src.connect(f).connect(g).connect(ctxA.destination);
      src.start();
      src.stop(ctxA.currentTime + dur);
    };
    const close = (): void => {
      if (ctxA) {
        try { void ctxA.close(); } catch { /* ignore */ }
        ctxA = null;
      }
    };
    return {
      shoot: () => blip(880, 0.12, "square", 0.06, 220),
      invaderShoot: () => blip(180, 0.18, "sawtooth", 0.05, 80),
      invaderDie: () => { noise(0.18, 0.12); blip(120, 0.18, "square", 0.05, 60); },
      playerDie: () => { noise(0.55, 0.2); blip(220, 0.6, "sawtooth", 0.08, 60); },
      ufo: () => blip(440, 0.08, "sine", 0.05, 660),
      ufoHit: () => { noise(0.25, 0.18); blip(660, 0.2, "square", 0.06, 220); },
      step: (n: number) => blip(80 + (n % 4) * 30, 0.05, "square", 0.04),
      toggleMute: () => { muted = !muted; return muted; },
      isMuted: () => muted,
      close,
    };
  })();

  function drawSprite(sprite: string[], x: number, y: number, scale: number, color: string): void {
    ctx!.fillStyle = color;
    for (let row = 0; row < sprite.length; row++) {
      const line = sprite[row]!;
      for (let col = 0; col < line.length; col++) {
        if (line[col] === "1") {
          ctx!.fillRect(Math.floor(x + col * scale), Math.floor(y + row * scale), scale, scale);
        }
      }
    }
  }

  // ---------- Bunkers ----------
  class Bunker {
    x: number;
    y: number;
    cells: boolean[][];
    constructor(x: number, y: number) {
      this.x = x; this.y = y;
      this.cells = BUNKER_SHAPE.map((r) => r.split("").map((c) => c === "1"));
    }
    get w(): number { return BUNKER_COLS * BUNKER_CELL; }
    get h(): number { return BUNKER_ROWS * BUNKER_CELL; }
    draw(): void {
      ctx!.fillStyle = "#2bd14b";
      for (let r = 0; r < BUNKER_ROWS; r++) {
        for (let c = 0; c < BUNKER_COLS; c++) {
          if (this.cells[r]![c]) {
            ctx!.fillRect(this.x + c * BUNKER_CELL, this.y + r * BUNKER_CELL, BUNKER_CELL, BUNKER_CELL);
          }
        }
      }
    }
    hit(px: number, py: number, pw: number, ph: number): boolean {
      const c0 = Math.floor((px - this.x) / BUNKER_CELL);
      const c1 = Math.floor((px + pw - 1 - this.x) / BUNKER_CELL);
      const r0 = Math.floor((py - this.y) / BUNKER_CELL);
      const r1 = Math.floor((py + ph - 1 - this.y) / BUNKER_CELL);
      let hit: { r: number; c: number } | null = null;
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          if (r >= 0 && r < BUNKER_ROWS && c >= 0 && c < BUNKER_COLS && this.cells[r]![c]) {
            hit = { r, c };
            break;
          }
        }
        if (hit) break;
      }
      if (!hit) return false;
      const splash: Array<[number, number]> = [
        [0, 0], [0, -1], [0, 1], [-1, 0], [1, 0],
        [-1, -1], [1, 1], [-1, 1], [1, -1],
      ];
      for (const [dr, dc] of splash) {
        if (Math.random() < 0.75) {
          const rr = hit.r + dr;
          const cc = hit.c + dc;
          if (rr >= 0 && rr < BUNKER_ROWS && cc >= 0 && cc < BUNKER_COLS) {
            this.cells[rr]![cc] = false;
          }
        }
      }
      return true;
    }
  }

  // ---------- Player ----------
  class Player {
    w = 10 * 3;
    h = 8 * 3;
    scale = 3;
    x = (W - 10 * 3) / 2;
    y = H - 80;
    speed = 280;
    cooldown = 0;
    dead = 0;
    invuln = 0;
    update(dt: number, input: { left: boolean; right: boolean }): void {
      if (this.dead > 0) { this.dead -= dt; return; }
      let vx = 0;
      if (input.left) vx -= 1;
      if (input.right) vx += 1;
      this.x += vx * this.speed * dt;
      if (this.x < 16) this.x = 16;
      if (this.x + this.w > W - 16) this.x = W - 16 - this.w;
      this.cooldown -= dt;
      if (this.invuln > 0) this.invuln -= dt;
    }
    canShoot(): boolean { return this.cooldown <= 0 && this.dead <= 0; }
    shoot(bullets: Bullet[]): void {
      if (!this.canShoot()) return;
      this.cooldown = 0.45;
      bullets.push({
        x: this.x + this.w / 2 - 2,
        y: this.y - 6,
        w: 3, h: 12,
        vy: -560,
        from: "player",
      });
      audio.shoot();
    }
    draw(): void {
      if (this.dead > 0) {
        const flicker = Math.floor(this.dead * 18) % 2 === 0;
        const s = flicker ? SPRITES.blast : SPRITES.player;
        drawSprite(s, this.x - (spriteWidth(s) - 10) * 1.5, this.y, this.scale, "#ff5a3c");
        return;
      }
      if (this.invuln > 0 && Math.floor(this.invuln * 12) % 2 === 0) return;
      drawSprite(SPRITES.player, this.x, this.y, this.scale, "#3cff6a");
      ctx!.fillStyle = "#3cff6a";
      ctx!.fillRect(12, H - 36, W - 24, 2);
    }
  }

  // ---------- Invader grid ----------
  class InvaderGrid {
    rows = 5;
    cols = 11;
    cellW = 36;
    cellH = 28;
    scale = 2.4;
    x: number;
    y: number;
    dir: 1 | -1 = 1;
    stepTimer = 0;
    frame: 0 | 1 = 0;
    stepIndex = 0;
    descend = 0;
    invaders: Invader[] = [];
    aliveCount: number;
    constructor(level: number) {
      this.x = (W - this.cols * this.cellW) / 2;
      this.y = 90 + Math.min(level - 1, 4) * 20;
      for (let r = 0; r < this.rows; r++) {
        for (let c = 0; c < this.cols; c++) {
          let type: InvaderType;
          let points: number;
          if (r === 0) { type = "squid"; points = 30; }
          else if (r <= 2) { type = "crab"; points = 20; }
          else { type = "octopus"; points = 10; }
          this.invaders.push({ alive: true, type, points, row: r, col: c });
        }
      }
      this.aliveCount = this.invaders.length;
    }
    invaderRect(inv: Invader): { x: number; y: number; w: number; h: number } {
      const sw = spriteWidth(SPRITES[inv.type][0]!) * this.scale;
      const sh = spriteHeight(SPRITES[inv.type][0]!) * this.scale;
      const cx = this.x + inv.col * this.cellW + this.cellW / 2;
      const cy = this.y + inv.row * this.cellH + this.cellH / 2;
      return { x: cx - sw / 2, y: cy - sh / 2, w: sw, h: sh };
    }
    bounds(): { minX: number; maxX: number; minY: number; maxY: number } {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const inv of this.invaders) {
        if (!inv.alive) continue;
        const r = this.invaderRect(inv);
        if (r.x < minX) minX = r.x;
        if (r.x + r.w > maxX) maxX = r.x + r.w;
        if (r.y < minY) minY = r.y;
        if (r.y + r.h > maxY) maxY = r.y + r.h;
      }
      return { minX, maxX, minY, maxY };
    }
    stepInterval(): number {
      return Math.max(0.04, 0.7 * (this.aliveCount / (this.rows * this.cols)));
    }
    update(dt: number, _level: number, onReachBottom: () => void): void {
      this.stepTimer += dt;
      const interval = this.stepInterval();
      if (this.stepTimer >= interval) {
        this.stepTimer = 0;
        this.frame = this.frame === 0 ? 1 : 0;
        this.stepIndex++;
        audio.step(this.stepIndex);
        const stepPx = 6;
        if (this.descend > 0) {
          this.y += stepPx;
          this.descend -= stepPx;
        } else {
          const b = this.bounds();
          const projectedMinX = b.minX + this.dir * stepPx;
          const projectedMaxX = b.maxX + this.dir * stepPx;
          if (projectedMaxX > W - 12 || projectedMinX < 12) {
            this.dir = this.dir === 1 ? -1 : 1;
            this.descend = stepPx * 3;
          } else {
            this.x += this.dir * stepPx;
          }
        }
        const b2 = this.bounds();
        if (b2.maxY > H - 80) onReachBottom();
      }
    }
    tryShoot(bullets: Bullet[], level: number): void {
      const cols: Record<number, Invader> = {};
      for (const inv of this.invaders) {
        if (!inv.alive) continue;
        const existing = cols[inv.col];
        if (!existing || inv.row > existing.row) cols[inv.col] = inv;
      }
      const shooters = Object.values(cols);
      if (shooters.length === 0) return;
      const inv = shooters[Math.floor(Math.random() * shooters.length)]!;
      const r = this.invaderRect(inv);
      bullets.push({
        x: r.x + r.w / 2 - 2,
        y: r.y + r.h,
        w: 3, h: 12,
        vy: 200 + level * 18,
        from: "enemy",
        wiggle: Math.random() * Math.PI * 2,
      });
      audio.invaderShoot();
    }
    draw(): void {
      for (const inv of this.invaders) {
        if (!inv.alive) continue;
        const sprite = SPRITES[inv.type][this.frame]!;
        const r = this.invaderRect(inv);
        let color = "#ffffff";
        if (inv.type === "squid") color = "#ff5fb1";
        else if (inv.type === "crab") color = "#5fd6ff";
        else if (inv.type === "octopus") color = "#9fff5f";
        drawSprite(sprite, r.x, r.y, this.scale, color);
      }
    }
  }

  // ---------- UFO ----------
  class UFO {
    active = false;
    x = 0;
    y = 30;
    scale = 2;
    w = spriteWidth(SPRITES.ufo) * 2;
    h = spriteHeight(SPRITES.ufo) * 2;
    dir: 1 | -1 = 1;
    speed = 120;
    spawnTimer = 12 + Math.random() * 8;
    update(dt: number): void {
      if (!this.active) {
        this.spawnTimer -= dt;
        if (this.spawnTimer <= 0) this.spawn();
        return;
      }
      this.x += this.dir * this.speed * dt;
      if ((this.dir > 0 && this.x > W + 8) || (this.dir < 0 && this.x + this.w < -8)) {
        this.active = false;
        this.spawnTimer = 14 + Math.random() * 10;
      }
    }
    spawn(): void {
      this.active = true;
      this.dir = Math.random() < 0.5 ? 1 : -1;
      this.x = this.dir > 0 ? -this.w : W;
      audio.ufo();
    }
    draw(): void {
      if (!this.active) return;
      drawSprite(SPRITES.ufo, this.x, this.y, this.scale, "#ff3a3a");
    }
  }

  // ---------- Particles / floaters / stars ----------
  const particles: Particle[] = [];
  function spawnExplosion(x: number, y: number, color: string, count = 14, spd = 130): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = spd * (0.4 + Math.random() * 0.9);
      particles.push({
        x, y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: 0.4 + Math.random() * 0.4,
        age: 0,
        color,
        size: 2 + Math.floor(Math.random() * 2),
      });
    }
  }
  function updateParticles(dt: number): void {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i]!;
      p.age += dt;
      if (p.age >= p.life) { particles.splice(i, 1); continue; }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 80 * dt;
    }
  }
  function drawParticles(): void {
    for (const p of particles) {
      const a = 1 - p.age / p.life;
      ctx!.globalAlpha = Math.max(0, a);
      ctx!.fillStyle = p.color;
      ctx!.fillRect(Math.floor(p.x), Math.floor(p.y), p.size, p.size);
    }
    ctx!.globalAlpha = 1;
  }

  const floaters: Floater[] = [];
  function spawnFloater(x: number, y: number, text: string, color = "#ffe45c"): void {
    floaters.push({ x, y, text, color, age: 0, life: 0.9 });
  }
  function updateFloaters(dt: number): void {
    for (let i = floaters.length - 1; i >= 0; i--) {
      const f = floaters[i]!;
      f.age += dt;
      f.y -= 26 * dt;
      if (f.age >= f.life) floaters.splice(i, 1);
    }
  }
  function drawFloaters(): void {
    ctx!.font = "bold 14px 'IBM Plex Mono', ui-monospace, monospace";
    ctx!.textAlign = "center";
    for (const f of floaters) {
      ctx!.globalAlpha = 1 - f.age / f.life;
      ctx!.fillStyle = f.color;
      ctx!.fillText(f.text, f.x, f.y);
    }
    ctx!.globalAlpha = 1;
    ctx!.textAlign = "start";
  }

  const stars: StarP[] = [];
  for (let i = 0; i < 70; i++) {
    stars.push({
      x: Math.random() * W,
      y: Math.random() * H,
      s: Math.random() * 1.6 + 0.3,
      v: 8 + Math.random() * 20,
    });
  }
  function updateStars(dt: number): void {
    for (const s of stars) {
      s.y += s.v * dt;
      if (s.y > H) { s.y = 0; s.x = Math.random() * W; }
    }
  }
  function drawStars(): void {
    for (const s of stars) {
      ctx!.fillStyle = s.s > 1.2 ? "#446" : "#223";
      ctx!.fillRect(Math.floor(s.x), Math.floor(s.y), Math.ceil(s.s), Math.ceil(s.s));
    }
  }

  // ---------- Game state ----------
  function makeBunkers(): Bunker[] {
    const count = 4;
    const bw = BUNKER_COLS * BUNKER_CELL;
    const gap = (W - count * bw) / (count + 1);
    const arr: Bunker[] = [];
    const by = H - 140;
    for (let i = 0; i < count; i++) {
      arr.push(new Bunker(Math.floor(gap + i * (bw + gap)), by));
    }
    return arr;
  }

  let state: StateValue = "title";
  let score = 0;
  let highScore = (() => {
    try { return Number(localStorage.getItem("si_high") ?? "0") || 0; }
    catch { return 0; }
  })();
  let lives = 3;
  let level = 1;
  let player = new Player();
  let grid = new InvaderGrid(1);
  let bunkers = makeBunkers();
  const ufo = new UFO();
  let bullets: Bullet[] = [];
  let invShootTimer = 1.0;
  let transitionTimer = 0;
  let screenShake = 0;

  function startWave(nextLevel: number): void {
    level = nextLevel;
    grid = new InvaderGrid(nextLevel);
    bullets = [];
    invShootTimer = Math.max(0.2, 1.0 - nextLevel * 0.08);
    if (nextLevel === 1) bunkers = makeBunkers();
  }

  function startNewGame(): void {
    state = "play";
    score = 0;
    lives = 3;
    level = 1;
    player = new Player();
    grid = new InvaderGrid(1);
    bunkers = makeBunkers();
    bullets = [];
    invShootTimer = 1.0;
    transitionTimer = 0;
    screenShake = 0;
    overlayEl.classList.add("hidden");
  }

  // ---------- Input ----------
  const input = { left: false, right: false, shootDown: false, shootEdge: false };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", " "].includes(e.key)) e.preventDefault();
    if (e.repeat) return;
    if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") input.left = true;
    if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") input.right = true;
    if (e.key === " ") { input.shootDown = true; input.shootEdge = true; }
    if (e.key === "p" || e.key === "P") togglePause();
    if (e.key === "m" || e.key === "M") audio.toggleMute();
    if (e.key === "Enter") handleEnter();
    if (e.key === "r" || e.key === "R") {
      if (state === "gameOver") startNewGame();
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") input.left = false;
    if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") input.right = false;
    if (e.key === " ") input.shootDown = false;
  };

  function handleEnter(): void {
    if (state === "title" || state === "gameOver") startNewGame();
  }
  function togglePause(): void {
    if (state === "play") state = "paused";
    else if (state === "paused") state = "play";
  }
  function onStartClick(): void {
    startNewGame();
  }

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  startBtn.addEventListener("click", onStartClick);

  // ---------- Collisions ----------
  function rectsOverlap(
    a: { x: number; y: number; w: number; h: number },
    b: { x: number; y: number; w: number; h: number },
  ): boolean {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
  }

  function killPlayer(fatal: boolean): void {
    audio.playerDie();
    spawnExplosion(player.x + player.w / 2, player.y + player.h / 2, "#ff5a3c", 36, 220);
    player.dead = 1.2;
    screenShake = 0.4;
    lives -= 1;
    state = "lifeLost";
    transitionTimer = 1.4;
    if (fatal) lives = 0;
  }

  function updatePlay(dt: number): void {
    player.update(dt, input);
    if (input.shootEdge) {
      player.shoot(bullets);
      input.shootEdge = false;
    }

    grid.update(dt, level, () => killPlayer(true));
    invShootTimer -= dt;
    if (invShootTimer <= 0) {
      grid.tryShoot(bullets, level);
      invShootTimer = Math.max(0.15, 1.1 - level * 0.07) * (0.6 + Math.random() * 0.9);
    }

    ufo.update(dt);

    for (let i = bullets.length - 1; i >= 0; i--) {
      const b = bullets[i]!;
      b.y += b.vy * dt;
      if (b.from === "enemy") {
        b.wiggle = (b.wiggle ?? 0) + dt * 8;
        b.x += Math.sin(b.wiggle) * 18 * dt;
      }
      if (b.y < -20 || b.y > H + 20) { bullets.splice(i, 1); continue; }

      let absorbed = false;
      for (const bunker of bunkers) {
        if (b.x < bunker.x + bunker.w && b.x + b.w > bunker.x &&
            b.y < bunker.y + bunker.h && b.y + b.h > bunker.y) {
          if (bunker.hit(b.x, b.y, b.w, b.h)) {
            absorbed = true;
            spawnExplosion(b.x + b.w / 2, b.y + b.h / 2, "#2bd14b", 5, 80);
            break;
          }
        }
      }
      if (absorbed) { bullets.splice(i, 1); continue; }

      if (b.from === "player") {
        let hit = false;
        for (const inv of grid.invaders) {
          if (!inv.alive) continue;
          const r = grid.invaderRect(inv);
          if (rectsOverlap(b, r)) {
            inv.alive = false;
            grid.aliveCount--;
            score += inv.points;
            spawnExplosion(r.x + r.w / 2, r.y + r.h / 2, "#ffd24a", 18, 160);
            spawnFloater(r.x + r.w / 2, r.y, "+" + inv.points);
            audio.invaderDie();
            hit = true;
            break;
          }
        }
        if (hit) { bullets.splice(i, 1); continue; }
        if (ufo.active) {
          const u = { x: ufo.x, y: ufo.y, w: ufo.w, h: ufo.h };
          if (rectsOverlap(b, u)) {
            const pts = [50, 100, 150, 200, 300][Math.floor(Math.random() * 5)]!;
            score += pts;
            spawnExplosion(u.x + u.w / 2, u.y + u.h / 2, "#ff5a3c", 28, 200);
            spawnFloater(u.x + u.w / 2, u.y, "+" + pts, "#ff9");
            audio.ufoHit();
            ufo.active = false;
            ufo.spawnTimer = 14 + Math.random() * 10;
            bullets.splice(i, 1);
            screenShake = 0.25;
            continue;
          }
        }
      } else {
        const p = { x: player.x, y: player.y, w: player.w, h: player.h };
        if (player.invuln <= 0 && player.dead <= 0 && rectsOverlap(b, p)) {
          bullets.splice(i, 1);
          killPlayer(false);
          continue;
        }
      }
    }

    updateParticles(dt);

    if (grid.aliveCount === 0) {
      state = "waveClear";
      transitionTimer = 1.8;
      spawnFloater(W / 2, H / 2 - 20, "WAVE " + level + " CLEAR", "#5fff9f");
    }
  }

  // ---------- Render ----------
  function drawCenterText(big: string, small: string): void {
    ctx!.fillStyle = "rgba(0,0,0,0.55)";
    ctx!.fillRect(0, H / 2 - 60, W, 120);
    ctx!.textAlign = "center";
    ctx!.fillStyle = "#fff";
    ctx!.font = "bold 36px 'IBM Plex Mono', ui-monospace, monospace";
    ctx!.fillText(big, W / 2, H / 2);
    ctx!.fillStyle = "#aaa";
    ctx!.font = "16px 'IBM Plex Mono', ui-monospace, monospace";
    ctx!.fillText(small, W / 2, H / 2 + 30);
    ctx!.textAlign = "left";
  }

  function drawGameOver(): void {
    ctx!.fillStyle = "rgba(0,0,0,0.65)";
    ctx!.fillRect(0, 0, W, H);
    ctx!.textAlign = "center";
    ctx!.fillStyle = "#ff5a3c";
    ctx!.font = "bold 56px 'IBM Plex Mono', ui-monospace, monospace";
    ctx!.fillText("GAME OVER", W / 2, H / 2 - 30);
    ctx!.fillStyle = "#fff";
    ctx!.font = "20px 'IBM Plex Mono', ui-monospace, monospace";
    ctx!.fillText("Final Score: " + score, W / 2, H / 2 + 10);
    ctx!.fillText("High Score: " + highScore, W / 2, H / 2 + 38);
    ctx!.fillStyle = "#ffe45c";
    ctx!.font = "16px 'IBM Plex Mono', ui-monospace, monospace";
    const blink = Math.floor(performance.now() / 500) % 2 === 0;
    if (blink) ctx!.fillText("PRESS ENTER (or R) TO RESTART", W / 2, H / 2 + 80);
    ctx!.textAlign = "left";
  }

  function render(): void {
    let sx = 0, sy = 0;
    if (screenShake > 0) {
      sx = (Math.random() - 0.5) * screenShake * 16;
      sy = (Math.random() - 0.5) * screenShake * 16;
    }
    ctx!.save();
    ctx!.translate(sx, sy);

    ctx!.fillStyle = "#000";
    ctx!.fillRect(0, 0, W, H);

    drawStars();

    ctx!.fillStyle = "#ddd";
    ctx!.font = "16px 'IBM Plex Mono', ui-monospace, monospace";
    ctx!.textAlign = "left";
    ctx!.fillText("SCORE  " + String(score).padStart(5, "0"), 16, 26);
    ctx!.textAlign = "center";
    ctx!.fillText("HI-SCORE  " + String(highScore).padStart(5, "0"), W / 2, 26);
    ctx!.textAlign = "right";
    ctx!.fillText("LEVEL  " + level, W - 16, 26);
    ctx!.textAlign = "left";

    ctx!.fillStyle = "#3cff6a";
    for (let i = 0; i < lives; i++) {
      const lx = 16 + i * (10 * 2 + 8);
      drawSprite(SPRITES.player, lx, H - 22, 2, "#3cff6a");
    }
    if (audio.isMuted()) {
      ctx!.fillStyle = "#a44";
      ctx!.font = "12px 'IBM Plex Mono', ui-monospace, monospace";
      ctx!.textAlign = "right";
      ctx!.fillText("MUTED", W - 16, H - 10);
      ctx!.textAlign = "left";
    }

    if (state !== "title") {
      for (const b of bunkers) b.draw();
      ufo.draw();
      grid.draw();
      player.draw();

      for (const b of bullets) {
        ctx!.fillStyle = b.from === "player" ? "#ffffff" : "#ff5a5a";
        ctx!.fillRect(Math.floor(b.x), Math.floor(b.y), b.w, b.h);
      }

      drawParticles();
      drawFloaters();
    }

    if (state === "paused") drawCenterText("PAUSED", "press P to resume");
    if (state === "waveClear") drawCenterText("WAVE " + level + " CLEAR", "get ready...");
    if (state === "lifeLost" && lives > 0) drawCenterText("LIFE LOST", String(lives) + " remaining");
    if (state === "gameOver") drawGameOver();

    ctx!.restore();
  }

  // ---------- Main loop ----------
  let lastTime = 0;
  let raf: number | null = null;
  function frame(t: number): void {
    const dt = Math.min(0.05, (t - lastTime) / 1000 || 0);
    lastTime = t;

    updateStars(dt);

    if (state === "play") {
      updatePlay(dt);
    } else if (state === "lifeLost") {
      transitionTimer -= dt;
      updateParticles(dt);
      if (transitionTimer <= 0) {
        if (lives <= 0) {
          state = "gameOver";
          if (score > highScore) {
            highScore = score;
            try { localStorage.setItem("si_high", String(highScore)); } catch { /* ignore */ }
          }
        } else {
          player = new Player();
          player.invuln = 1.5;
          state = "play";
        }
      }
    } else if (state === "waveClear") {
      transitionTimer -= dt;
      updateParticles(dt);
      if (transitionTimer <= 0) {
        startWave(level + 1);
        state = "play";
      }
    }

    updateFloaters(dt);

    render();
    if (screenShake > 0) screenShake -= dt;
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  cleanupFn = (): void => {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    startBtn.removeEventListener("click", onStartClick);
    audio.close();
  };
}

function unmount(): void {
  if (cleanupFn) cleanupFn();
  cleanupFn = null;
  if (wrap) wrap.remove();
  wrap = null;
}

export const spaceInvaders: Game = {
  id: "space-invaders",
  title: "Space Invaders",
  description: "Pixel-art classic. Arrows to move, space to shoot.",
  mount,
  unmount,
};
